import { signOut } from 'firebase/auth';
import { subirFotoPerfilUsuario } from '../data/perfilFotoRepository.js';
import { firebaseErrorHintEs } from '../../../core/errors/firebaseErrorHintEs.js';
import { openNotificacionesResenaModal } from '../../../core/ui/notificacionesResenaModal.js';
import { countPendientesResenaNotif } from '../../../core/ui/mlvResenaNotifBadges.js';
import { MLV_Z } from '../../../core/ui/mlvZLayers.js';
import { perfilRatingTarjetaCopy } from '../../../core/reputation/reputacionVendedor.js';
import { buildWhatsAppUrl } from '../../../core/contact/whatsappLink.js';
import { openNegocioVecinoExplainer } from '../../../core/ui/negocioVecinoExplainer.js';
import { mlvTrack } from '../../../core/telemetry/mlvTrack.js';

const WA_EMPUJA_COMERCIO_TEL = '1162399695';
const WA_EMPUJA_COMERCIO_MSJ =
  'Hola. Quiero contratar un espacio publicitario para mi negocio.';

function openEmpujaComercioWhatsApp() {
  mlvTrack('empuja_comercio_perfil_wa', {});
  const url =
    buildWhatsAppUrl(WA_EMPUJA_COMERCIO_TEL, WA_EMPUJA_COMERCIO_MSJ) ||
    `https://wa.me/5491162399695?text=${encodeURIComponent(WA_EMPUJA_COMERCIO_MSJ)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Pantalla de perfil: carga datos y conecta acciones con navegación centralizada.
 * `appCtx.navigation` se completa al final del bootstrap.
 */
export function createPerfilScreen({
  getFirebase,
  perfilPresenter,
  vendedorRepository,
  isProfileComplete,
  vendedorPresenter,
  appCtx,
  getLoginView,
  calificacionesRepository,
  notificacionesRepository,
  catalogoRepository,
  getNotificacionesItems = () => [],
  getDeferredInstallPrompt,
  clearDeferredInstallPrompt,
  ejecutarValidacionUbicacionGps = null,
}) {
  async function renderProfileView() {
    const user = getFirebase().auth.currentUser;
    if (!user) {
      getLoginView()();
      return;
    }
    let profile = null;
    let ventasCount = 0;
    let nivelResumen = null;
    try {
      profile = await vendedorRepository.syncUserProfile(user.uid, user.email);
      nivelResumen = vendedorRepository.resumenNivelVendedor(profile);
      ventasCount = nivelResumen?.ventasEnVentana ?? 0;
    } catch (e) {
      console.error(e);
    }

    const nav = appCtx.navigation;

    await user.getIdToken(true);
    const idTokenResult = await user.getIdTokenResult();

    const { ratingDisplay, ratingSubline } = perfilRatingTarjetaCopy(profile);

    perfilPresenter.showPerfil({
      user,
      profile,
      isVerified: Boolean(profile && isProfileComplete(profile)),
      ventasCount,
      nivelResumen,
      onVerInfoExperienciaVendedor: () => nav.pushVendedorExperienciaInfo(),
      onVerPlanPremiumProfesional: () => void nav.pushPremiumPlan(),
      onComoFuncionaBarrio: () => void openNegocioVecinoExplainer({ section: 'cupos' }),
      onEmpujaComercio: () => void openEmpujaComercioWhatsApp(),
      onServiciosBarrio: () => void nav.pushServiciosHome(),
      ratingDisplay,
      ratingSubline,
      onEditarPerfil: async () => {
        const fresh = await vendedorRepository.syncUserProfile(user.uid, user.email);
        if (!isProfileComplete(fresh)) {
          nav.pushVendedorDatos();
          vendedorPresenter.datosVendedorView(fresh);
          return;
        }
        nav.pushVendedorCambiarTelefono();
        await vendedorPresenter.showCambiarTelefonoContacto(fresh, {
          onBack: () => nav.goBack(),
        });
      },
      onMisPublicaciones: async () => {
        nav.pushVendedorMis();
        await vendedorPresenter.showMisPublicaciones(
          user.uid,
          nav.misPublicacionesBackOpts()
        );
      },
      onPublicar: async () => {
        nav.goToVendedorPublicar();
        await vendedorPresenter.showPublicarForm();
      },
      showAdminPanel: Boolean(idTokenResult.claims?.admin === true),
      onAdminPanel: () => nav.pushAdminShow(),
      onSearch: () => nav.replaceCatalog(),
      onExplorar: () => nav.replaceCatalog(),
      onVender: () => void nav.openSellOrOnboarding(),
      onLogout: async () => {
        await signOut(getFirebase().auth);
        nav.replaceLoginShow();
      },
      onCambiarFoto: async (file) => {
        try {
          await subirFotoPerfilUsuario({ getFirebase, file });
          await getFirebase().auth.currentUser.reload();
          await renderProfileView();
        } catch (e) {
          const hint = firebaseErrorHintEs(e);
          const base = e?.message || 'No se pudo subir la foto. Intentá de nuevo.';
          const msg = hint ? `${base} ${hint}` : base;
          const errEl = document.createElement('div');
          errEl.className =
            'fixed bottom-4 left-4 right-4 rounded-2xl border border-red-200 bg-red-50/95 px-4 py-3 text-sm text-red-900 shadow-lg';
          errEl.style.zIndex = String(MLV_Z.navErrorToast);
          errEl.textContent = msg;
          document.body.appendChild(errEl);
          setTimeout(() => errEl.remove(), 5000);
          await renderProfileView();
        }
      },
      notificacionesPendientesCount: countPendientesResenaNotif(getNotificacionesItems()),
      onNotificaciones: async () => {
        await openNotificacionesResenaModal({
          items: [...getNotificacionesItems()],
          calificacionesRepository,
          invalidateCatalogoCache: () => catalogoRepository?.invalidateCatalogoCache?.(),
          marcarLeida: (uid, nid) => notificacionesRepository?.marcarLeida(uid, nid),
          uidComprador: user.uid,
          ejecutarValidacionUbicacionGps,
        });
      },
      showInstalarApp:
        typeof getDeferredInstallPrompt === 'function' && getDeferredInstallPrompt() != null,
      onInstalarApp: async () => {
        const ev = getDeferredInstallPrompt?.();
        if (!ev?.prompt) return;
        ev.prompt();
        try {
          await ev.userChoice;
        } catch (_e) {
          /* ignore */
        }
        clearDeferredInstallPrompt?.();
        await renderProfileView();
      },
    });
  }

  function showProfileView() {
    void renderProfileView();
  }

  return { renderProfileView, showProfileView };
}
